// src/pages/mantenimiento/TabPlanMensual.jsx
// F-MTO-01, en 2 pasos: (1) qué equipos aplican este mes, (2) en qué
// semana(s). "Realizado" se calcula solo, revisando si hay un Ticket
// Preventivo cerrado de ese equipo en esa semana — todo cuelga de
// F-MTO-03, no hay checkbox aparte.
import { useEffect, useState } from 'react';
import api from '../../services/api';
import styles from './estilos';

const MESES_NOMBRE = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const MESES_ABREV = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function semanaDeFecha(f) {
  const inicio = new Date(f.getFullYear(), 0, 1);
  return Math.ceil(((f - inicio) / 86400000 + inicio.getDay() + 1) / 7);
}

/** Encuentra el lunes-domingo real que corresponde a una semana/año, usando la misma numeración que el backend. */
function rangoDeSemana(anio, semana) {
  const inicioAnio = new Date(anio, 0, 1);
  const offset = inicioAnio.getDay();
  let fecha = new Date(anio, 0, 1 + (semana - 1) * 7 - offset);
  while (semanaDeFecha(fecha) > semana) fecha.setDate(fecha.getDate() - 1);
  while (semanaDeFecha(fecha) < semana) fecha.setDate(fecha.getDate() + 1);
  const fin = new Date(fecha);
  fin.setDate(fin.getDate() + 6);
  const fmt = (d) => `${d.getDate()} ${MESES_ABREV[d.getMonth()]}`;
  return `${fmt(fecha)} – ${fmt(fin)}`;
}

function TabPlanMensual() {
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [paso, setPaso] = useState('equipos');

  return (
    <div>
      <p style={styles.subtitulo}>
        Plan anual de mantenimiento preventivo (F-MTO-01) — se ajusta mes a mes. "Realizado" se marca solo cuando cierras un Ticket Preventivo (F-MTO-03) de ese equipo esa semana.
      </p>
      <div style={styles.filtrosBar}>
        <div style={styles.filtroGrupo}>
          <label style={styles.filtroLabel}>Año</label>
          <input type="number" value={anio} onChange={(e) => setAnio(e.target.value)} style={{ ...styles.filtroInput, width: '80px' }} />
        </div>
        <div style={styles.filtroGrupo}>
          <label style={styles.filtroLabel}>Mes</label>
          <select value={mes} onChange={(e) => setMes(e.target.value)} style={styles.filtroInput}>
            {MESES_NOMBRE.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <button onClick={() => setPaso('equipos')} style={{ ...styles.subTabBtn, ...(paso === 'equipos' ? styles.subTabBtnActivo : {}) }}>1. Equipos del mes</button>
        <button onClick={() => setPaso('semanas')} style={{ ...styles.subTabBtn, ...(paso === 'semanas' ? styles.subTabBtnActivo : {}) }}>2. Semanas</button>
      </div>

      {paso === 'equipos' ? <PasoEquipos anio={anio} mes={mes} /> : <PasoSemanas anio={anio} mes={mes} />}
    </div>
  );
}

function PasoEquipos({ anio, mes }) {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState('');

  async function cargar() {
    try {
      setCargando(true);
      const res = await api.get('/api/mantenimiento/plan-mes-equipos', { params: { anio, mes } });
      setFilas(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }
  useEffect(() => { cargar(); }, [anio, mes]); // eslint-disable-line react-hooks/exhaustive-deps

  async function toggleIncluido(equipoId, valorActual) {
    setFilas((prev) => prev.map((f) => (f.id === equipoId ? { ...f, incluido: !valorActual } : f)));
    try {
      await api.post('/api/mantenimiento/plan-mes-equipos', { equipo_id: equipoId, anio, mes, incluido: !valorActual });
    } catch (err) {
      console.error(err);
      cargar();
    }
  }

  const filasFiltradas = filas.filter((f) => !filtro || f.area.toLowerCase().includes(filtro.toLowerCase()) || f.descripcion.toLowerCase().includes(filtro.toLowerCase()));
  const totalIncluidos = filas.filter((f) => f.incluido).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: '#6E4F80' }}>{totalIncluidos} de {filas.length} equipos incluidos este mes</span>
        <input type="text" placeholder="Buscar equipo o área..." value={filtro} onChange={(e) => setFiltro(e.target.value)} style={{ ...styles.filtroInput, width: 220 }} />
      </div>
      {cargando ? <div>Cargando...</div> : (
        <div style={styles.tablaScroll}>
          <table style={styles.tabla}>
            <thead><tr><th style={styles.th}>Incluir</th><th style={styles.th}>Área</th><th style={styles.th}>Equipo</th></tr></thead>
            <tbody>
              {filasFiltradas.map((f) => (
                <tr key={f.id}>
                  <td style={styles.td}><input type="checkbox" checked={f.incluido} onChange={() => toggleIncluido(f.id, f.incluido)} /></td>
                  <td style={styles.td}>{f.area}</td>
                  <td style={styles.td}>{f.codigo ? `${f.codigo} — ` : ''}{f.descripcion}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filasFiltradas.length === 0 && <p style={{ padding: 16, color: '#8A7690', fontSize: 12 }}>Sin resultados.</p>}
        </div>
      )}
    </div>
  );
}

function PasoSemanas({ anio, mes }) {
  const [filas, setFilas] = useState([]);
  const [semanas, setSemanas] = useState([]);
  const [cargando, setCargando] = useState(true);

  async function cargar() {
    try {
      setCargando(true);
      const res = await api.get('/api/mantenimiento/plan-mensual', { params: { anio, mes } });
      setFilas(res.data.data);
      setSemanas(res.data.semanas);
    } catch (err) {
      console.error(err);
    } finally {
      setCargando(false);
    }
  }
  useEffect(() => { cargar(); }, [anio, mes]); // eslint-disable-line react-hooks/exhaustive-deps

  async function toggleProgramado(equipoId, semana, valorActual) {
    try {
      await api.post('/api/mantenimiento/plan-semanal', { equipo_id: equipoId, anio, semana, programado: !valorActual });
      cargar();
    } catch (err) {
      console.error(err);
    }
  }

  const totalesPorSemana = semanas.map((s) => {
    const programados = filas.filter((f) => f.semanas.find((x) => x.semana === s)?.programado).length;
    const realizados = filas.filter((f) => {
      const celda = f.semanas.find((x) => x.semana === s);
      return celda?.programado && celda?.realizado;
    }).length;
    return { semana: s, programados, realizados, pct: programados > 0 ? Math.round((realizados / programados) * 100) : null };
  });

  if (!cargando && filas.length === 0) {
    return (
      <p style={{ color: '#8A7690', fontSize: 13, padding: 16, background: '#fff', borderRadius: 10, border: '1px solid #E6DDD2' }}>
        Todavía no hay equipos incluidos en el paso 1 para este mes — ve a "1. Equipos del mes" y selecciona cuáles aplican.
      </p>
    );
  }

  return (
    <div>
      {cargando ? <div>Cargando...</div> : (
        <div style={styles.tablaScroll}>
          <table style={styles.tabla}>
            <thead>
              <tr>
                <th style={styles.th}>Equipo</th><th style={styles.th}>Área</th>
                {semanas.map((s) => (
                  <th key={s} style={{ ...styles.th, textAlign: 'center' }}>
                    Sem {s}
                    <div style={{ fontSize: 9, fontWeight: 400, textTransform: 'none', color: '#8A7690', marginTop: 2 }}>
                      {rangoDeSemana(anio, s)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.id}>
                  <td style={styles.td}>{f.codigo ? `${f.codigo} — ` : ''}{f.descripcion}</td>
                  <td style={styles.td}>{f.area}</td>
                  {f.semanas.map((c) => (
                    <td key={c.semana} style={{ ...styles.td, textAlign: 'center' }}>
                      <input type="checkbox" checked={c.programado} onChange={() => toggleProgramado(f.id, c.semana, c.programado)} title="Marcar si está programado esta semana" />
                      {c.programado && (
                        c.realizado
                          ? <span style={{ color: '#3E7C5A', marginLeft: 4 }} title="Tiene Ticket Preventivo cerrado esta semana">✓</span>
                          : <span style={{ color: '#C08A2B', marginLeft: 4 }} title="Programado, sin Ticket cerrado todavía">⏳</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ ...styles.td, fontWeight: 700 }} colSpan={2}>% Cumplimiento semanal</td>
                {totalesPorSemana.map((t) => (
                  <td key={t.semana} style={{ ...styles.td, textAlign: 'center', fontWeight: 700, color: t.pct === null ? '#8A7690' : t.pct >= 95 ? '#3E7C5A' : t.pct >= 70 ? '#C08A2B' : '#B23A48' }}>
                    {t.pct !== null ? `${t.pct}%` : '—'}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
          <p style={{ fontSize: 11, color: '#8A7690', padding: '10px 4px 0' }}>
            ✓ = tiene Ticket Preventivo (F-MTO-03) cerrado esa semana · ⏳ = programado, todavía sin ticket cerrado
          </p>
        </div>
      )}
    </div>
  );
}

export default TabPlanMensual;